<template>
  <div id="pie-chart" class="widget widget-height">
    <div class="title row-block">
      <h1>{{ $tr('analytics.widgets.pie.title') }}</h1>

      <img
        v-tooltip="{
          content: $tr('analytics.widgets.pie.helper'),
          placement: 'top-center',
          classes: 'tight'
        }"
        class="icon-first canvas-filter"
        src="/static/images/elements/icon_info.svg"
        alt="Info"
      />
      <img class="canvas-filter" src="/static/images/elements/icon_download.svg" alt="Download" @click="saveAsPng" />
    </div>

    <div class="main-content">
      <div v-if="loading" class="spinner-wraper">
        <div class="spinner"></div>
      </div>

      <template v-else>
        <div v-if="emptyData" class="placeholder">
          <img src="/static/images/elements/analytics/widget-empty.svg" alt="No data" />

          <p>{{ $tr('analytics.widgets.pie.nooneWaiting') }}</p>
        </div>

        <template v-else>
          <pie-chart class="pie-container" :chart-data="datacollection" :options="pieOptions" />

          <ul class="statuses">
            <li v-for="status in statuses" :key="status.id" class="row-block">
              <div class="circle" :style="{ backgroundColor: status.color }"></div>
              <p class="name">{{ status.name }}</p>
              <p class="count">{{ status.count }}</p>
            </li>
          </ul>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { colors } from './colorPalettes.js';
import PieChart from './PieChart.js';
import mixinSaveToPng from './mixinSaveToPng.js';

export default {
  name: 'WidgetPieChart',

  components: {
    PieChart
  },

  mixins: [mixinSaveToPng],

  props: {
    filtersParams: Object
  },

  data() {
    return {
      datacollection: null,
      pieOptions: {
        legend: {
          display: false
        },

        cutoutPercentage: 75,
        tooltips: {
          backgroundColor: '#333333',
          bodyFontSize: 16,
          cornerRadius: 6,
          xPadding: 8,
          yPadding: 9,
          caretSize: 0,

          enabled: false,
          custom: function (tooltipModel) {
            // Tooltip Element
            let tooltipEl = document.getElementById('chartjs-tooltip');

            // Create element on first render
            if (!tooltipEl) {
              tooltipEl = document.createElement('div');
              tooltipEl.id = 'chartjs-tooltip';
              this._chart.canvas.parentNode.appendChild(tooltipEl);
            }

            // Hide if no tooltip
            if (tooltipModel.opacity === 0) {
              tooltipEl.style.opacity = 0;
              return;
            }

            // Set Text
            if (tooltipModel.body) {
              const label = tooltipModel.body[0].lines[0].split(':').map(s => s.trim());

              tooltipEl.innerHTML = `<div class="tooltip-container">
                <div class="circle" style="background-color: ${tooltipModel.labelColors[0].backgroundColor}"></div>
                <p class="name">${label[0]}</p>
                <p class="count">${label[1]}</p>
              </div>`;
            }

            // Display, position, and set styles for font
            tooltipEl.style.opacity = 1;
            tooltipEl.style.position = 'absolute';
            tooltipEl.style.left = tooltipModel.x + 'px';
            tooltipEl.style.top = tooltipModel.y + 'px';
            tooltipEl.style.padding = tooltipModel.yPadding + 'px ' + tooltipModel.xPadding + 'px';
            tooltipEl.style.backgroundColor = tooltipModel.backgroundColor;
            tooltipEl.style.borderRadius = tooltipModel.cornerRadius + 'px';
          }
        }
      }
    };
  },

  computed: {
    statuses() {
      const copyStatuses = this.$store.getters['analytics/pieChart/statuses'].slice();

      return copyStatuses
        .sort((a, b) => a.priority - b.priority)
        .map((s, idx) => {
          return Object.assign({}, s, {
            color: colors[idx]
          });
        });
    },

    emptyData() {
      return this.statuses ? this.statuses.every(s => s.count === 0) : true;
    },

    ...mapGetters({
      loading: 'analytics/pieChart/loading'
    })
  },

  watch: {
    filtersParams() {
      this.fetch();
    },

    statuses(newVal) {
      this.fillData(newVal);
    }
  },

  mounted() {
    this.fillData(this.statuses);
  },

  beforeMount() {
    this.fetch();
  },

  methods: {
    fetch() {
      this.$store.dispatch('analytics/pieChart/fetch', this.filtersParams);
    },

    saveAsPng() {
      this.mixinSaveAsPng({
        filename: `pie-chart_${this.filtersParams.created_after}_${this.filtersParams.created_before}`,
        saveId: 'pie-chart',
        filterClass: 'canvas-filter'
      });
    },

    fillData(statuses) {
      const filteredStatuses = statuses.filter(s => s.count !== 0);

      this.datacollection = {
        labels: filteredStatuses.map(s => s.name),
        datasets: [
          {
            data: filteredStatuses.map(s => s.count),
            backgroundColor: filteredStatuses.map(s => s.color),
            borderWidth: 0
          }
        ]
      };
    }
  }
};
</script>

<style lang="scss">
.tooltip-container {
  display: flex;
  align-items: center;

  .circle {
    width: 10px;
    height: 10px;
    border-radius: 50%;
  }

  p {
    font-size: 14px;
    margin: 0;
    white-space: nowrap;
    color: #fff;

    &.name {
      margin-left: 10px;
    }
    &.count {
      margin-left: 20px;
    }
  }
}
</style>

<style lang="scss" scoped>
.widget .main-content {
  .description {
    margin-top: 6px;
    min-height: 48px;
  }

  .pie-container {
    margin: 45px auto 0;
    display: block;
    width: 180px;
    height: 180px;
    position: relative;
  }

  .statuses {
    margin: 40px 0 5px;

    .circle {
      width: 10px;
      height: 10px;
      border-radius: 50%;
    }

    p {
      font-size: 15px;
      line-height: 22px;

      &.name {
        margin-left: 12px;
        max-width: 190px;
      }
      &.count {
        margin-left: auto;
      }
    }

    .row-block {
      margin-bottom: 18px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .placeholder {
    text-align: center;

    img {
      margin: 42px auto 44px;
    }
  }

  .spinner-wraper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100px;
  }

  .spinner {
    margin: auto;
    height: 50px;
    width: 50px;
    top: 0;
  }
}
</style>
